import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Eye, TrendingUp } from "lucide-react";
import AppHeader from "../components/AppHeader";
import "./Account.css";

export default function AccountViews() {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isRTL = i18n.language?.startsWith('ar');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchViews = async () => {
      try {
        // iter345 — use the canonical analytics endpoint so Views page
        // Total, Today, and By Ad always match Account → Overview and
        // Performance Insights.
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/analytics/overview`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        if (response.ok) {
          const json = await response.json();
          // Map to the local schema this component was built against.
          setData({
            total_views: json.total_views || 0,
            today_views: json.views_today || 0,
            by_ad: (json.by_ad || []).map(r => ({
              ad_id: r.ad_id,
              title: r.title,
              views_total: r.views,
              views_today: r.views_today,
            })),
          });
        } else {
          setError(t('accoview.failedToLoadViews'));
        }
      } catch (e) {
        setError(t('accoview.failedToLoadViews'));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchViews();
    } else {
      setLoading(false);
      setError(t('accoresp.pleaseLogIn'));
    }
  }, [token, isRTL]);

  return (
    <div className="account-page" style={{ minHeight: '100vh', paddingBottom: 100 }}>
      <AppHeader
        title={t('accoview.views')}
        backFallback="/account"
        testId="account-views-header"
      />

      <div style={{ padding: 16, maxWidth: 600, margin: '0 auto' }}>
        {error && (
          <div style={{ 
            padding: 12, 
            borderRadius: 12, 
            background: '#FEF2F2', 
            color: '#DC2626',
            marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ height: 80, background: '#F3F4F6', borderRadius: 16, animation: 'pulse 2s infinite' }} />
            <div style={{ height: 200, background: '#F3F4F6', borderRadius: 16, animation: 'pulse 2s infinite' }} />
          </div>
        ) : data ? (
          <>
            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              <div
                data-testid="views-page-total-tile"
                style={{ 
                  padding: 16, 
                  borderRadius: 16, 
                  background: '#fff', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Eye size={16} style={{ color: '#6B7280' }} />
                  <span style={{ fontSize: 13, color: '#6B7280' }}>
                    {t('accoview.total')}
                  </span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#111' }}>
                  {(data.total_views ?? 0).toLocaleString()}
                </div>
              </div>
              
              <div
                data-testid="views-page-today-tile"
                style={{ 
                  padding: 16, 
                  borderRadius: 16, 
                  background: '#fff', 
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <TrendingUp size={16} style={{ color: '#10B981' }} />
                  <span style={{ fontSize: 13, color: '#6B7280' }}>
                    {t('whp.today')}
                  </span>
                </div>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10B981' }}>
                  {(data.today_views ?? 0).toLocaleString()}
                </div>
              </div>
            </div>

            {/* By Ad List */}
            <div style={{ 
              padding: 16, 
              borderRadius: 16, 
              background: '#fff', 
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #E5E7EB',
            }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
                {t('accoview.byAd')}
              </h3>

              {(() => {
                // iter345 — Only show ads that actually have views. If
                // total_views==0 (truly no views anywhere), show the
                // empty state. Otherwise show every ad that has >0
                // views ordered by view count desc (already sorted by
                // the backend).
                const rows = (data.by_ad || []).filter(r => (r.views_total || 0) > 0);
                if (rows.length === 0) {
                  return (
                    <div
                      data-testid="views-empty"
                      style={{ 
                        textAlign: 'center', 
                        padding: 24, 
                        color: '#6B7280',
                        fontSize: 14,
                      }}
                    >
                      <Eye size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                      <p style={{ margin: 0 }}>
                        {t('accoview.noViewsDataYet')}
                      </p>
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: '#9CA3AF' }}>
                        {t('accoview.viewsWillAppearHereWhen')}
                      </p>
                    </div>
                  );
                }
                return (
                  <div
                    data-testid="views-by-ad-list"
                    style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                  >
                    {rows.map((row) => (
                      <div
                        key={row.ad_id}
                        data-testid={`views-by-ad-row-${row.ad_id}`}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: 12,
                          borderRadius: 12,
                          background: '#F9FAFB',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/ad/${row.ad_id}`)}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ 
                            fontWeight: 500, 
                            fontSize: 14,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}>
                            {row.title}
                          </div>
                        </div>
                        <div style={{ textAlign: isRTL ? 'left' : 'right', marginLeft: isRTL ? 0 : 12, marginRight: isRTL ? 12 : 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 16 }}>{row.views_total}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>
                            {isRTL ? `اليوم ${row.views_today}` : `today ${row.views_today}`}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
